/*
 * Copyright (c) 2021, salesforce.com, inc.
 * SPDX-License-Identifier: BSD-3-Clause
 *
 * Trending product row for SearchIdlePanel — list (header modal + search page mobile)
 * or grid (search results page desktop xl+). Styles come from SearchIdlePanel theme variant.
 */
import React from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Flex,
  Image,
  Text,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {getMasterTileImages} from '@amblique/king-living/app/utils/product-images';

const TrendingProductRow = ({
  product,
  styles,
  layout,
  onPickProduct,
  productTypeLabel,
}) => {
  const name = product?.name ?? product?.productName ?? '';
  const img = getMasterTileImages(product).find(
    (item) => item.mediaType !== 'video' && (item.disBaseLink || item.link)
  );
  const src = img?.disBaseLink || img?.link || null;

  const primaryCategoryName = String(product?.c_primaryCategoryName ?? '').trim();
  const typeLabel = primaryCategoryName ?
    `${productTypeLabel} / ${primaryCategoryName}` :
    productTypeLabel;

  const isGrid = layout === 'grid';

  const itemStyles = isGrid ? styles.trendingGridItem : styles.trendingItem;
  const innerStyles = isGrid ? styles.trendingGridItemInner : styles.trendingItemInner;
  const imageStyles = isGrid ? styles.trendingGridImage : styles.trendingImage;
  const infoStyles = isGrid ? styles.trendingGridInfo : styles.trendingInfo;
  const titleStyles = isGrid ? styles.trendingGridTitle : styles.trendingTitle;
  const categoryStyles = isGrid ? styles.trendingGridCategory : styles.trendingCategory;

  const Inner = isGrid ? Box : Flex;

  return (
    <Button
      h="auto"
      minH="unset"
      p={0}
      variant="unstyled"
      w="full"
      {...itemStyles}
      onMouseDown={(e) => {
        e.preventDefault();
        onPickProduct(product);
      }}
    >
      <Inner {...innerStyles}>
        <Box {...imageStyles}>
          {src ? (
            <Image
              alt=""
              h={isGrid ? 'full' : '48px'}
              objectFit={isGrid ? 'contain' : 'cover'}
              src={src}
              w={isGrid ? 'full' : '108px'}
            />
          ) : null}
        </Box>
        <Flex {...infoStyles}>
          <Text {...titleStyles}>
            {name}
          </Text>
          <Text {...categoryStyles} noOfLines={1}>
            {typeLabel}
          </Text>
        </Flex>
      </Inner>
    </Button>
  );
};

TrendingProductRow.propTypes = {
  layout: PropTypes.oneOf(['grid', 'list']).isRequired,
  product: PropTypes.object.isRequired,
  productTypeLabel: PropTypes.string.isRequired,
  styles: PropTypes.object.isRequired,
  onPickProduct: PropTypes.func.isRequired,
};

export default React.memo(TrendingProductRow);
